import React, { useState, useEffect } from 'react';
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
// tabs removed in favor of single page layout
// removed table in favor of cards layout
import CircularProgress from "@mui/joy/CircularProgress";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Input from "@mui/joy/Input";
import Button from "@mui/joy/Button";
import { LineChart } from "@mui/x-charts/LineChart";
import { PaymentSummaryApi } from './paymentSummaryApi';
import { StatsRangeRequest, TimeGranularity, SummaryResponse, SummaryRow, GroupedSummaryResponse, GroupedSummaryRow } from '../../../../models/paymentSummary';
import {OperationType} from "../../../../models/payments";
import { formatRubles, kopecksToRublesNumber } from '../../../../common/money';

export function PaymentMethodsStatsTab() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<SummaryResponse | null>(null);
    const [groupedData, setGroupedData] = useState<GroupedSummaryResponse | null>(null);
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [granularity, setGranularity] = useState<TimeGranularity>(TimeGranularity.All);

    // Фильтры для графиков
    const [chartFromDate, setChartFromDate] = useState<string>('');
    const [chartToDate, setChartToDate] = useState<string>('');
    const [chartGranularity, setChartGranularity] = useState<TimeGranularity>(TimeGranularity.Day);
    const [chartLoading, setChartLoading] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const request: StatsRangeRequest = {
                fromUtc: fromDate || undefined,
                toUtc: toDate || undefined,
                granularity
            };
            const summaryResponse = await PaymentSummaryApi.getSummary(request);
            setData(summaryResponse);
        } catch (error) {
            console.error('Error loading payment summary:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadChartData = async () => {
        setChartLoading(true);
        try {
            const request: StatsRangeRequest = {
                fromUtc: chartFromDate || undefined,
                toUtc: chartToDate || undefined,
                granularity: chartGranularity
            };
            const groupedResponse = await PaymentSummaryApi.getGroupedSummary(request);
            setGroupedData(groupedResponse);
        } catch (error) {
            console.error('Error loading grouped payment summary:', error);
        } finally {
            setChartLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        loadChartData();
    }, []);

    

    const getDepositRows = (): SummaryRow[] => {
        return data?.rows.filter(row => row.operation === OperationType.Deposit) || [];
    };

    const getWithdrawalRows = (): SummaryRow[] => {
        return data?.rows.filter(row => row.operation === OperationType.Withdraw) || [];
    };

    const getDepositGroupedRows = (): GroupedSummaryRow[] => {
        return groupedData?.rows.filter(row => row.operation === OperationType.Deposit) || [];
    };

    const getWithdrawalGroupedRows = (): GroupedSummaryRow[] => {
        return groupedData?.rows.filter(row => row.operation === OperationType.Withdraw) || [];
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const prepareChartData = (rows: GroupedSummaryRow[]) => {
        // Группируем данные по дате
        const groupedByDate = rows.reduce((acc, row) => {
            const dateKey = formatDate(row.bucketUtc);
            if (!acc[dateKey]) {
                acc[dateKey] = {};
            }
            acc[dateKey][`method_${row.paymentMethodId}`] = kopecksToRublesNumber(row.sumWithCommission); // конвертируем копейки в рубли
            acc[dateKey][`count_${row.paymentMethodId}`] = row.count;
            return acc;
        }, {} as Record<string, Record<string, number>>);

        // Преобразуем в массив для LineChart
        return Object.entries(groupedByDate)
            .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
            .map(([date, methods]) => ({
                date,
                ...methods
            }));
    };

    const getUniquePaymentMethods = (rows: GroupedSummaryRow[]) => {
        const uniqueMethods = new Map<number, string>();
        rows.forEach(row => {
            uniqueMethods.set(row.paymentMethodId, row.paymentMethodTitle);
        });
        return Array.from(uniqueMethods.entries());
    };

    const prepareCombinedChartData = (rows: GroupedSummaryRow[]) => {
        // Группируем данные по дате и типу операции
        const groupedByDate = rows.reduce((acc, row) => {
            const dateKey = formatDate(row.bucketUtc);
            if (!acc[dateKey]) {
                acc[dateKey] = { deposits: 0, withdrawals: 0 };
            }
            
            if (row.operation === 'deposit') {
                acc[dateKey].deposits += kopecksToRublesNumber(row.sumWithCommission); // конвертируем копейки в рубли
            } else if (row.operation === 'withdraw') {
                acc[dateKey].withdrawals += kopecksToRublesNumber(row.sumWithCommission);
            }
            
            return acc;
        }, {} as Record<string, { deposits: number; withdrawals: number }>);

        // Преобразуем в массив для LineChart
        return Object.entries(groupedByDate)
            .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
            .map(([date, totals]) => ({
                date,
                deposits: totals.deposits,
                withdrawals: totals.withdrawals
            }));
    };

    const renderLineChart = (rows: GroupedSummaryRow[], title: string) => {
        if (rows.length === 0) {
            return (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    Нет данных для отображения
                </Box>
            );
        }

        const chartData = prepareChartData(rows);
        const uniqueMethods = getUniquePaymentMethods(rows);
        
        // Создаем серии для каждого метода оплаты
        const series = uniqueMethods.map(([methodId, methodTitle]) => ({
            dataKey: `method_${methodId}`,
            label: methodTitle,
        }));

        return (
            <Box sx={{ mb: 3 }}>
                <Typography level="title-md" sx={{ mb: 2 }}>
                    {title}
                </Typography>
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'background.level1',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <LineChart
                        dataset={chartData}
                        xAxis={[
                            {
                                dataKey: 'date',
                                scaleType: 'band',
                            },
                        ]}
                        series={series}
                        height={300}
                    />
                </Box>
            </Box>
        );
    };

    const renderCombinedChart = (rows: GroupedSummaryRow[], title: string) => {
        if (rows.length === 0) {
            return (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    Нет данных для отображения
                </Box>
            );
        }

        const chartData = prepareCombinedChartData(rows);
        
        return (
            <Box sx={{ mb: 3 }}>
                <Typography level="title-md" sx={{ mb: 2 }}>
                    {title}
                </Typography>
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'background.level1',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <LineChart
                        dataset={chartData}
                        xAxis={[
                            {
                                dataKey: 'date',
                                scaleType: 'band',
                            },
                        ]}
                        series={[
                            {
                                dataKey: 'deposits',
                                label: 'Пополнения (руб.)',
                                color: '#2e7d32', // зеленый цвет
                            },
                            {
                                dataKey: 'withdrawals',
                                label: 'Выводы (руб.)',
                                color: '#d32f2f', // красный цвет
                            }
                        ]}
                        height={300}
                    />
                </Box>
            </Box>
        );
    };

    const renderCards = (rows: SummaryRow[], title: string) => (
        <Box sx={{ mb: 3 }}>
            <Typography level="title-md" sx={{ mb: 2 }}>
                {title}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 1.5
                }}
            >
                {rows.map((row, index) => (
                    <Box
                        key={index}
                        sx={{
                            p: 1.5,
                            borderRadius: '8px',
                            bgcolor: 'background.level1',
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Typography level="title-sm" sx={{ mb: 1 }}>
                            {row.paymentMethodTitle}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography level="body-sm" color="neutral">
                                Сумма:  {formatRubles(row.sumWithCommission)}
                            </Typography>
                            <Typography level="body-sm" color="neutral">
                                Процент: {row.percent.toFixed(2)}%
                            </Typography>
                            <Typography level="body-sm" color="neutral">
                                Количество: {row.count}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ p: 2 }}>
            <Typography level="h4" sx={{ mb: 3 }}>
                Статистика методов оплаты
            </Typography>

            {/* Фильтры */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    p: 2,
                    bgcolor: 'background.level1',
                    borderRadius: '8px',
                    mb: 3
                }}
            >
                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>От даты</FormLabel>
                    <Input
                        type="datetime-local"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                    />
                </FormControl>

                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>До даты</FormLabel>
                    <Input
                        type="datetime-local"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                    />
                </FormControl>

                

                <Button onClick={loadData} loading={loading} sx={{ alignSelf: 'end' }}>
                    Обновить
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size="lg" />
                </Box>
            ) : data ? (
                <>
                    <Box
                        sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: '8px',
                            bgcolor: 'background.level1',
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Typography level="title-md" sx={{ mb: 1 }}>
                            Итого
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ minWidth: 220 }}>
                                <Typography level="body-sm" color="neutral">
                                    Общая сумма пополнений
                                </Typography>
                                <Typography level="title-md" color="success">
                                    {formatRubles(data.totalDepositSum)}
                                </Typography>
                            </Box>
                            <Box sx={{ minWidth: 220 }}>
                                <Typography level="body-sm" color="neutral">
                                    Общая сумма выводов
                                </Typography>
                                <Typography level="title-md" color="danger">
                                    {formatRubles(data.totalWithdrawalSum)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {renderCards(getDepositRows(), 'Депозиты')}

                    {renderCards(getWithdrawalRows(), 'Выводы')}

                    {/* Графики */}
                    <Box sx={{ mt: 4 }}>
                        <Typography level="h4" sx={{ mb: 3 }}>
                            Графики пополнений и выводов
                        </Typography>

                        {/* Фильтры для графиков */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                p: 2,
                                bgcolor: 'background.level1',
                                borderRadius: '8px',
                                mb: 3
                            }}
                        >
                            <FormControl size="sm" sx={{ minWidth: 150 }}>
                                <FormLabel>От даты</FormLabel>
                                <Input
                                    type="datetime-local"
                                    value={chartFromDate}
                                    onChange={(e) => setChartFromDate(e.target.value)}
                                />
                            </FormControl>

                            <FormControl size="sm" sx={{ minWidth: 150 }}>
                                <FormLabel>До даты</FormLabel>
                                <Input
                                    type="datetime-local"
                                    value={chartToDate}
                                    onChange={(e) => setChartToDate(e.target.value)}
                                />
                            </FormControl>

                            <FormControl size="sm" sx={{ minWidth: 120 }}>
                                <FormLabel>Группировка</FormLabel>
                                <Select
                                    value={chartGranularity}
                                    onChange={(event, newValue) => setChartGranularity(newValue as TimeGranularity)}
                                >
                                    <Option value={TimeGranularity.Hour}>По часам</Option>
                                    <Option value={TimeGranularity.Day}>По дням</Option>
                                    <Option value={TimeGranularity.Month}>По месяцам</Option>
                                    <Option value={TimeGranularity.Year}>По годам</Option>
                                    <Option value={TimeGranularity.All}>За все время</Option>
                                </Select>
                            </FormControl>

                            <Button onClick={loadChartData} loading={chartLoading} sx={{ alignSelf: 'end' }}>
                                Обновить графики
                            </Button>
                        </Box>

                        {chartLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress size="lg" />
                            </Box>
                        ) : groupedData ? (
                            <>
                                {renderLineChart(getDepositGroupedRows(), 'График пополнений')}
                                
                                {renderLineChart(getWithdrawalGroupedRows(), 'График выводов')}
                            </>
                        ) : (
                            <Typography level="body-md" color="neutral">
                                Нет данных для отображения графиков
                            </Typography>
                        )}
                    </Box>
                </>
            ) : (
                <Typography level="body-md" color="neutral">
                    Нет данных для отображения
                </Typography>
            )}
        </Box>
    );
}

